/**
 * YouTube Playables (PlayCube) — ytgame SDK lifecycle, cloud save, audio, host pause.
 * Non-YouTube builds set VITE_WITH_YT=false so this module never touches window.ytgame.
 */

import {
  createYtGameStorage,
  flushYtGameStorageNow,
  hydrateYtGameStorage,
  teardownYtGameStorage,
} from "./yt-persistence.js";
import { resolveYtRewardPlacementId } from "../config/ad-placements.js";
import { normalizeLeaderboardScore } from "../leaderboard/leaderboard-client.js";

var initPromise = null;
var enabled = false;
var audioSyncInstalled = false;
var pauseSyncInstalled = false;
var hostPauseDepth = 0;

function isYtBuild() {
  return typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_WITH_YT === "true";
}

function getYtGame() {
  if (!isYtBuild() || typeof window === "undefined") return null;
  return window.ytgame || null;
}

function waitForYtGame(timeoutMs) {
  var limit = timeoutMs != null ? timeoutMs : 15000;
  return new Promise(function (resolve) {
    var existing = getYtGame();
    if (existing) {
      resolve(existing);
      return;
    }
    var settled = false;
    function finish(result) {
      if (settled) return;
      settled = true;
      resolve(result);
    }
    var elapsed = 0;
    var step = 50;
    var timer = setInterval(function () {
      var g = getYtGame();
      if (g) {
        clearInterval(timer);
        finish(g);
        return;
      }
      elapsed += step;
      if (elapsed >= limit) {
        clearInterval(timer);
        finish(null);
      }
    }, step);
  });
}

function refreshEnabled() {
  var g = getYtGame();
  enabled = !!(isYtBuild() && g && g.game && g.system);
  return enabled;
}

async function saveFullGameData(data) {
  var g = getYtGame();
  if (!enabled || !g || !g.game || typeof g.game.saveData !== "function") return;
  try {
    await g.game.saveData(data);
  } catch (e) {
    console.warn("[YtBridge] saveData failed:", e);
    throw e;
  }
}

async function loadFullGameData() {
  var g = getYtGame();
  if (!enabled || !g || !g.game || typeof g.game.loadData !== "function") return null;
  try {
    return await g.game.loadData();
  } catch (e) {
    console.warn("[YtBridge] loadData failed:", e);
    return null;
  }
}

function applyCombinedMute(soundManager) {
  if (!soundManager || typeof soundManager.syncOutputMuteState !== "function") return;
  soundManager.syncOutputMuteState();
}

export const YtBridge = {
  ensureInitialized: function () {
    if (!isYtBuild()) {
      enabled = false;
      return Promise.resolve();
    }
    if (initPromise) return initPromise;

    initPromise = waitForYtGame().then(async function (g) {
      if (!g) {
        enabled = false;
        console.warn("[YtBridge] ytgame not available");
        return;
      }
      refreshEnabled();
      if (!enabled) return;

      createYtGameStorage(function (payload) {
        return saveFullGameData(payload);
      });

      await hydrateYtGameStorage(loadFullGameData);
    }).catch(function (err) {
      enabled = false;
      teardownYtGameStorage();
      console.warn("[YtBridge] init failed:", err);
    });

    return initPromise;
  },

  isEnabled: function () {
    return !!enabled;
  },

  notifyFirstFrameReady: function () {
    if (!enabled) return;
    var g = getYtGame();
    if (!g || !g.game || typeof g.game.firstFrameReady !== "function") return;
    try {
      g.game.firstFrameReady();
    } catch (e) {
      console.warn("[YtBridge] firstFrameReady failed:", e);
    }
  },

  notifyGameReady: function () {
    if (!enabled) return;
    var g = getYtGame();
    if (!g || !g.game || typeof g.game.gameReady !== "function") return;
    try {
      g.game.gameReady();
    } catch (e) {
      console.warn("[YtBridge] gameReady failed:", e);
    }
  },

  /**
   * @param {{ setPlatformMuted?: (muted: boolean) => void, syncOutputMuteState?: () => void }} soundManager
   */
  installAudioSync: function (soundManager) {
    if (audioSyncInstalled) return;
    if (!soundManager || typeof soundManager.setPlatformMuted !== "function") return;

    YtBridge.ensureInitialized().then(function () {
      if (audioSyncInstalled) return;
      if (!enabled) return;
      var g = getYtGame();
      if (!g || !g.system || typeof g.system.isAudioEnabled !== "function") return;

      audioSyncInstalled = true;

      function applyHostAudio() {
        var on = false;
        try {
          on = g.system.isAudioEnabled() === true;
        } catch (e0) {
          console.warn("[YtBridge] isAudioEnabled failed:", e0);
        }
        soundManager.setPlatformMuted(!on);
        applyCombinedMute(soundManager);
      }

      applyHostAudio();

      if (typeof g.system.onAudioEnabledChange === "function") {
        try {
          g.system.onAudioEnabledChange(function (isAudioEnabled) {
            soundManager.setPlatformMuted(!isAudioEnabled);
            applyCombinedMute(soundManager);
          });
        } catch (e) {
          console.warn("[YtBridge] onAudioEnabledChange failed:", e);
        }
      }
    });
  },

  /**
   * @param {{ setHostPaused?: (paused: boolean) => void }} game
   */
  installPauseSync: function (game) {
    if (pauseSyncInstalled) return;
    if (!game || typeof game.setHostPaused !== "function") return;

    YtBridge.ensureInitialized().then(function () {
      if (pauseSyncInstalled) return;
      if (!enabled) return;
      var g = getYtGame();
      if (!g || !g.system) return;

      pauseSyncInstalled = true;
      hostPauseDepth = 0;

      function setHostPausedFromDepth() {
        game.setHostPaused(hostPauseDepth > 0);
      }

      if (typeof g.system.onPause === "function") {
        try {
          g.system.onPause(function () {
            hostPauseDepth += 1;
            setHostPausedFromDepth();
          });
        } catch (e) {
          console.warn("[YtBridge] onPause failed:", e);
        }
      }

      if (typeof g.system.onResume === "function") {
        try {
          g.system.onResume(function () {
            hostPauseDepth = Math.max(0, hostPauseDepth - 1);
            setHostPausedFromDepth();
          });
        } catch (e2) {
          console.warn("[YtBridge] onResume failed:", e2);
        }
      }
    });
  },

  /** Flush debounced cloud save after a progress checkpoint (storage override already updated). */
  relayProgressSaved: function () {
    if (!enabled) return Promise.resolve();
    return flushYtGameStorageNow();
  },

  /**
   * Report the player's best score to YouTube engagement (must match cloud-save best).
   * @param {number} score
   * @returns {Promise<boolean>}
   */
  sendEngagementScore: function (score) {
    if (!enabled) return Promise.resolve(false);
    var g = getYtGame();
    if (!g || !g.engagement || typeof g.engagement.sendScore !== "function") {
      return Promise.resolve(false);
    }
    var value = normalizeLeaderboardScore(score);
    if (value <= 0) return Promise.resolve(false);
    try {
      var result = g.engagement.sendScore({ value: value });
      if (result && typeof result.then === "function") {
        return result
          .then(function () {
            return true;
          })
          .catch(function (err) {
            console.warn("[YtBridge] sendScore failed:", err);
            return false;
          });
      }
      return Promise.resolve(true);
    } catch (e) {
      console.warn("[YtBridge] sendScore failed:", e);
      return Promise.resolve(false);
    }
  },

  /** True when `ytgame.ads.requestRewardedAd` is available on this build. */
  canOfferRewardedAds: function () {
    if (!enabled) return false;
    var g = getYtGame();
    return !!(g && g.ads && typeof g.ads.requestRewardedAd === "function");
  },

  /** True when `ytgame.ads.requestInterstitialAd` is available on this build. */
  canRequestInterstitialAd: function () {
    if (!enabled) return false;
    var g = getYtGame();
    return !!(g && g.ads && typeof g.ads.requestInterstitialAd === "function");
  },

  /**
   * Play Again interstitial whenever YouTube ads are available.
   * Not gated on `runIndex` — the first lifetime Play Again still qualifies.
   * @param {object | null | undefined} game
   * @returns {boolean}
   */
  shouldShowPlayAgainInterstitial: function (game) {
    return !!(enabled && game);
  },

  /**
   * Request an interstitial. YouTube makes no guarantee an ad is shown.
   * Suspends the game via host-pause while the request is in flight (Mediacube /
   * Playables guidance), then always resumes — even on reject/unavailable.
   * @param {{ game?: { setHostPaused?: (paused: boolean) => void, isHostPaused?: boolean } }} [options]
   * @returns {Promise<boolean>} true when the SDK request resolved successfully
   */
  requestInterstitialAd: function (options) {
    var opts = options || {};
    var game = opts.game || null;

    return YtBridge.ensureInitialized().then(function () {
      if (!enabled) return false;
      var g = getYtGame();
      if (!g || !g.ads || typeof g.ads.requestInterstitialAd !== "function") {
        return false;
      }

      var pausedByUs = false;
      if (game && typeof game.setHostPaused === "function") {
        hostPauseDepth += 1;
        pausedByUs = true;
        game.setHostPaused(true);
      }

      function releasePause() {
        if (!pausedByUs || !game) return;
        pausedByUs = false;
        hostPauseDepth = Math.max(0, hostPauseDepth - 1);
        if (typeof game.setHostPaused === "function") {
          game.setHostPaused(hostPauseDepth > 0);
        }
      }

      return g.ads
        .requestInterstitialAd()
        .then(function () {
          releasePause();
          return true;
        })
        .catch(function (err) {
          console.warn("[YtBridge] requestInterstitialAd failed:", err);
          releasePause();
          return false;
        });
    });
  },

  /**
   * Rewarded video. Resolves `{ granted, cancelled }`. Grant only when YouTube
   * reports earned (`true`). Cancel/dismiss is `{ granted: false, cancelled: true }`.
   * SDK reject / unavailable is `{ granted: false, cancelled: false }`.
   * @param {{
   *   name?: string,
   *   onStarted?: () => void,
   *   onFinished?: () => void,
   *   onError?: (err: unknown) => void
   * }} [options]
   * @returns {Promise<{ granted: boolean, cancelled: boolean }>}
   */
  requestRewardedAd: function (options) {
    var opts = options || {};
    var placementName = resolveYtRewardPlacementId(opts.name);
    var started = typeof opts.onStarted === "function" ? opts.onStarted : null;
    var finished = typeof opts.onFinished === "function" ? opts.onFinished : null;
    var onError = typeof opts.onError === "function" ? opts.onError : null;

    return YtBridge.ensureInitialized().then(function () {
      if (!enabled) {
        if (onError) onError(new Error("YouTube reward ad unavailable"));
        return { granted: false, cancelled: false };
      }
      var g = getYtGame();
      if (!g || !g.ads || typeof g.ads.requestRewardedAd !== "function") {
        if (onError) onError(new Error("YouTube reward ad unavailable"));
        return { granted: false, cancelled: false };
      }
      if (started) started();
      return g.ads
        .requestRewardedAd(placementName)
        .then(function (earned) {
          if (finished) finished();
          var granted = earned === true;
          return { granted: granted, cancelled: !granted };
        })
        .catch(function (err) {
          console.warn("[YtBridge] requestRewardedAd failed:", err);
          if (onError) onError(err);
          return { granted: false, cancelled: false };
        });
    });
  },
};
